import dayjs from "dayjs";
import { Granularity, Kpi, KpiComparisonMode } from "@acto/taskpane/core/domain";
import { Kpi as GqlKpi } from "@acto/gql/graphql";
import { toGqlKpi } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import {
  formatDayMonthRange,
  formatNumericDateRange,
  formatKpiValue,
  formatMonthYear,
  formatMonthYearRange,
  formatProfitWithMargin,
  formatWeekdayDateLong,
} from "@acto/taskpane/field-sales-2/utils/format";
import {
  addDaysUtc,
  createCalendarFormat,
  isoToUtc,
  isoWeekNumber,
  quarterOfUtc,
  toIsoDate,
} from "@acto/taskpane/field-sales-2/components/primitives/Calendar/dateUtils";
import type { BarChart, DateRangeWindow, YSeries } from "@acto/genui-components-schema";
import type { TrendBarChartProps } from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import type { TrendComparisonProps } from "@acto/taskpane/field-sales-2/components/primitives/TrendComparison";
import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";

const X_FIELD = "period";
const TOOLTIP_LABEL_FIELD = "periodLabel";
const CURRENT_FIELD = "current";
const PREVIOUS_FIELD = "previous";
const KPI_SERIES_LABEL_KEY: Partial<Record<Kpi, string>> = {
  [Kpi.REVENUE]: "revenueTab",
  [Kpi.MARGIN]: "marginTab",
  [Kpi.ORDERS]: "ordersTab",
};

/** Tab-label key (in the `kpi` namespace) for the KPI; PROFIT has no own tab and reads as revenue. */
export function kpiTabLabelKey(kpi: Kpi): string {
  return KPI_SERIES_LABEL_KEY[kpi] ?? "revenueTab";
}

/**
 * Builds a per-bar labeller for the given granularity. `axis` is the compact x-tick, `tooltip` the
 * fuller title, `focus` the headline label shown while the bar is selected, always carrying the year
 * (a range can straddle a year boundary). Months stay on dayjs (locale set globally); day/week use
 * Intl at UTC to match the backend's date-only buckets.
 */
const barLabeller = (
  granularity: Granularity,
  locale: string,
): ((iso: string) => { axis: string; tooltip: string; focus: string }) => {
  if (granularity === Granularity.MONTHLY) {
    return (iso) => ({
      axis: dayjs(iso).format("MMM"),
      tooltip: dayjs(iso).format("MMMM"),
      focus: formatMonthYear(iso, locale),
    });
  }

  if (granularity === Granularity.QUARTERLY) {
    return (iso) => {
      const start = isoToUtc(iso);
      const quarter = quarterOfUtc(start);
      const label = `Q${quarter} ${start.getUTCFullYear()}`;

      return { axis: `Q${quarter}`, tooltip: label, focus: label };
    };
  }

  if (granularity === Granularity.YEARLY) {
    return (iso) => {
      const year = String(isoToUtc(iso).getUTCFullYear());

      return { axis: year, tooltip: year, focus: year };
    };
  }

  const dayMonth = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" });

  if (granularity === Granularity.WEEKLY) {
    const { weekRange } = createCalendarFormat(locale);
    // "KW NN" reads naturally only in German; other locales keep the date label.
    const useCalendarWeek = locale.toLowerCase().startsWith("de");
    return (iso) => {
      const start = isoToUtc(iso);
      const axis = useCalendarWeek ? `KW ${isoWeekNumber(start)}` : dayMonth.format(start);

      return {
        axis,
        tooltip: weekRange(start, addDaysUtc(start, 6)),
        focus: formatDayMonthRange({ start: iso, end: toIsoDate(addDaysUtc(start, 6)) }, locale),
      };
    };
  }

  // DAILY: day-of-month axis, full weekday + date tooltip.
  const fullDay = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
  return (iso) => {
    const day = isoToUtc(iso);

    return { axis: dayMonth.format(day), tooltip: fullDay.format(day), focus: formatWeekdayDateLong(iso, locale) };
  };
};

interface DataPointData {
  periodStart: string;
  value: number;
  /**
   * Start of the comparison bucket this point is paired with. `null`: that bucket has no date.
   * Absent: the date is taken from the comparison series at the same position.
   */
  previousPeriodStart?: string | null;
}

interface TimeSeriesData {
  dataPoints: DataPointData[];
}

interface ComparisonData {
  totalValue: number;
  change: { percentageChange: number | null } | null;
  range: { startDate: string; endDate: string };
}

export interface KpiSummaryValueData {
  kpi: GqlKpi;
  totalValue: number;
  timeSeries: TimeSeriesData;
  comparison: ComparisonData | null;
  comparisonTimeSeries: TimeSeriesData | null;
}

export interface KpiTrendBucket {
  /** Bucket label including the year, e.g. "Juni 2026". A range can straddle a year boundary. */
  label: string;
  /** Bucket value, formatted like {@link KpiTrend.total} (margin annotation included). */
  total: string;
  /** Comparison block scoped to this bucket's pair bar. Undefined exactly when {@link KpiTrend.comparison} is. */
  comparison: TrendComparisonProps | undefined;
}

/** Unformatted KPI numbers behind the card, for consumers that need values rather than display strings (e.g. the ambient-context envelope). */
export interface KpiTrendRaw {
  totalValue: number;
  previousTotalValue?: number;
  percentageChange?: number | null;
  /** Chart buckets paired like the rendered bars: bucket i carries comparison bucket i. */
  series: { periodStart: string; value: number; previousValue?: number }[];
}

export interface KpiTrend {
  isPending: boolean;
  /** True once the query has resolved with no current-period datapoints. */
  isEmpty: boolean;
  rangeLabel: string;
  total: string;
  comparison: TrendComparisonProps | undefined;
  chart: TrendBarChartProps;
  /** Per-bar focus view-models, aligned with `chart.data`: bucket i belongs to bar i. */
  buckets: KpiTrendBucket[];
  /** Set once the query resolved with a main value; undefined while pending/empty. */
  raw: KpiTrendRaw | undefined;
}

export interface BuildKpiTrendInput {
  values: KpiSummaryValueData[];
  fetching: boolean;
  stale: boolean;
  selectedKpi: Kpi;
  /** Reads MARGIN as its own ratio series instead of profit annotated with the ratio. */
  marginAsRatio?: boolean;
  comparisonMode: KpiComparisonMode;
  dateRange?: { startDate: string; endDate: string } | null;
  isCustomRange: boolean;
  granularity: Granularity;
  locale: string;
  currency: CurrencyCode;
  tKpi: (key: string) => string;
}

export function buildKpiTrend({
  values,
  fetching,
  stale,
  selectedKpi,
  marginAsRatio = false,
  comparisonMode,
  dateRange,
  isCustomRange,
  granularity,
  locale,
  currency,
  tKpi,
}: BuildKpiTrendInput): KpiTrend {
  const isMargin = selectedKpi === Kpi.MARGIN && !marginAsRatio;
  const displayKpi = isMargin ? Kpi.PROFIT : selectedKpi;
  const gqlDisplayKpi = toGqlKpi(displayKpi);

  const mainValue = values.find((value) => value.kpi === gqlDisplayKpi);
  const marginValue = isMargin ? values.find((value) => value.kpi === GqlKpi.Margin) : undefined;
  const currentSeries = mainValue?.timeSeries?.dataPoints ?? [];
  const previousSeries = mainValue?.comparisonTimeSeries?.dataPoints ?? [];

  const orderedCurrent = [...currentSeries].sort((a, b) => a.periodStart.localeCompare(b.periodStart));
  const orderedPrevious = [...previousSeries].sort((a, b) => a.periodStart.localeCompare(b.periodStart));

  const labelFor = barLabeller(granularity, locale);

  const marginByPeriod = new Map(
    (marginValue?.timeSeries?.dataPoints ?? []).map((point) => [point.periodStart, point.value]),
  );
  const previousMarginByPeriod = new Map(
    (marginValue?.comparisonTimeSeries?.dataPoints ?? []).map((point) => [point.periodStart, point.value]),
  );

  const formatSeriesValue = (value: number, options?: { compact?: boolean }) =>
    formatKpiValue(displayKpi, value, currency, locale, options?.compact ?? false);

  const formatHeadline = (mainTotal: number, marginRatio: number | null) =>
    isMargin ? formatProfitWithMargin(mainTotal, marginRatio, currency, locale) : formatSeriesValue(mainTotal);

  // Comparison values pair by position, and must keep doing so: year-over-year pairs January 2026
  // with January 2025, so the two period starts deliberately differ. Both series are gap-filled
  // server-side at the same granularity. A differing bucket count (e.g. a YoY week-boundary shift)
  // degrades gracefully: surplus current bars pair with 0. A point's own previousPeriodStart, when
  // present, dates its comparison instead of the positional pair.
  const bars = orderedCurrent.map((point, index) => {
    const { axis, tooltip, focus } = labelFor(point.periodStart);

    return {
      periodStart: point.periodStart,
      value: point.value,
      previousPeriodStart:
        point.previousPeriodStart === undefined
          ? orderedPrevious[index]?.periodStart
          : (point.previousPeriodStart ?? undefined),
      previousValue: orderedPrevious[index]?.value,
      axisLabel: axis,
      tooltipLabel: tooltip,
      focusLabel: focus,
      focusTotal: formatHeadline(point.value, marginByPeriod.get(point.periodStart) ?? null),
    };
  });

  // x key is the period start, unique per bar (see TrendBarChart's xField): axis labels repeat across a year boundary.
  const data: BarChart["data"] = bars.map((bar) => ({
    [X_FIELD]: bar.periodStart,
    [TOOLTIP_LABEL_FIELD]: bar.tooltipLabel,
    [CURRENT_FIELD]: bar.value,
    [PREVIOUS_FIELD]: bar.previousValue ?? 0,
  }));

  const periodStarts = bars.map((bar) => bar.periodStart);
  const axisLabels = bars.map((bar) => bar.axisLabel);

  const range: DateRangeWindow = {
    start: periodStarts[0] ?? "",
    end: periodStarts[periodStarts.length - 1] ?? "",
  };

  // A custom range reads as an explicit day-precision window ("01.10.2025 – 30.10.2025"); a preset
  // period reads as a month span ("Oktober – Oktober 2025"). The current and comparison labels must
  // share precision, otherwise a custom selection shows an explicit current window above a
  // month-name comparison.
  const formatWindow = (window: { start: string; end: string }): string =>
    isCustomRange ? formatNumericDateRange(window, locale) : formatMonthYearRange(window, locale);

  // Any non-zero point counts: a loss-making period's margin ratio is negative but still a comparison.
  const hasComparison = previousSeries.some((p) => p.value !== 0);
  const comparisonData = mainValue?.comparison;
  const seriesLabel = tKpi(kpiTabLabelKey(selectedKpi));

  const comparisonLabel =
    comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? tKpi("previousPeriod") : tKpi("previousYear");

  const leadSeries: YSeries = { field: CURRENT_FIELD, label: seriesLabel };
  const comparisonYSeries: YSeries = { field: PREVIOUS_FIELD, label: comparisonLabel, style: "comparison" };

  const comparison: TrendComparisonProps | undefined = comparisonData
    ? {
        label: comparisonLabel,
        rangeLabel: formatWindow({ start: comparisonData.range.startDate, end: comparisonData.range.endDate }),
        value: formatHeadline(comparisonData.totalValue, marginValue?.comparison?.totalValue ?? null),
        trendLabel: tKpi("trend"),
        trendPercent:
          comparisonData.change?.percentageChange != null ? Math.round(comparisonData.change.percentageChange) : null,
      }
    : undefined;

  const buckets: KpiTrendBucket[] = bars.map((bar) => {
    const previousValue = bar.previousValue ?? 0;
    const previousMargin =
      bar.previousPeriodStart !== undefined ? (previousMarginByPeriod.get(bar.previousPeriodStart) ?? null) : null;

    return {
      label: bar.focusLabel,
      total: bar.focusTotal,
      comparison: comparison && {
        label: comparisonLabel,
        rangeLabel: bar.previousPeriodStart !== undefined ? labelFor(bar.previousPeriodStart).focus : "",
        value: formatHeadline(previousValue, previousMargin),
        trendLabel: tKpi("trend"),
        // Important, a 0 or missing baseline yields no trend, never a fabricated percentage.
        trendPercent: previousValue > 0 ? Math.round(((bar.value - previousValue) / previousValue) * 100) : null,
      },
    };
  });

  return {
    isPending: fetching || stale,
    isEmpty: !fetching && !stale && bars.length === 0,
    rangeLabel: formatWindow(dateRange ? { start: dateRange.startDate, end: dateRange.endDate } : range),
    total: formatHeadline(mainValue?.totalValue ?? 0, marginValue?.totalValue ?? null),
    comparison,
    buckets,
    chart: {
      data,
      xField: X_FIELD,
      leadSeries,
      comparisonSeries: hasComparison ? comparisonYSeries : undefined,
      periodStarts,
      axisLabels,
      currency,
      locale,
      tooltipLabelField: TOOLTIP_LABEL_FIELD,
      valueFormatter: formatSeriesValue,
    },
    raw: mainValue
      ? {
          totalValue: mainValue.totalValue,
          previousTotalValue: comparisonData?.totalValue,
          percentageChange: comparisonData?.change?.percentageChange,
          series: bars.map(({ periodStart, value, previousValue }) => ({ periodStart, value, previousValue })),
        }
      : undefined,
  };
}
